import React, { useEffect, useState } from "react";
import api from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Textarea } from "@/components/ui/textarea";
import { CheckSquare, Plus, Check, X, Clock, Gift, Percent, CalendarPlus, Users } from "lucide-react";
import { toast } from "sonner";

const KIND_META = {
  trial_extension: { icon: CalendarPlus, label: "Trial Extension", tint: "text-blue-700 bg-blue-100 border-blue-400/25" },
  discount:        { icon: Percent,      label: "Discount",         tint: "text-amber-700 bg-amber-100 border-amber-400/25" },
  complementary:   { icon: Gift,         label: "Complementary",    tint: "text-purple-700 bg-purple-100 border-purple-400/25" },
};

const STATUS_META = {
  pending:  "text-amber-700 bg-amber-100",
  approved: "text-emerald-700 bg-emerald-100",
  rejected: "text-red-700 bg-red-100",
};

export default function AdminApprovals() {
  const [items, setItems] = useState([]);
  const [status, setStatus] = useState("pending");
  const [loading, setLoading] = useState(true);
  const [entities, setEntities] = useState([]);

  const [openNew, setOpenNew] = useState(false);
  const [newKind, setNewKind] = useState("trial_extension");
  const [newForm, setNewForm] = useState({ org_id: "", days: 7, discount_pct: 10, valid_until: "", reason: "" });

  // Bulk-discount state
  const [openBulk, setOpenBulk] = useState(false);
  const [bulkForm, setBulkForm] = useState({
    discount_pct: 20, valid_until: "", reason: "",
    plan_ids: [], entity_type: "", name_contains: "", created_after: "", created_before: "",
  });
  const [bulkPreview, setBulkPreview] = useState(null);
  const [bulkBusy, setBulkBusy] = useState(false);

  const load = () => {
    setLoading(true);
    api.get(`/admin/approvals?status=${status}`)
      .then((r) => setItems(r.data?.approvals || []))
      .finally(() => setLoading(false));
  };
  useEffect(load, [status]);
  useEffect(() => { api.get("/admin/entities").then((r) => setEntities(r.data?.entities || [])).catch(() => {}); }, []);

  const decide = async (a, decision) => {
    const note = window.prompt(`${decision === "approved" ? "Approve" : "Reject"} — optional note?`, "") || "";
    try {
      await api.post(`/admin/approvals/${a.id}/decide`, { decision, note });
      toast.success(`Marked ${decision}`);
      load();
    } catch (err) { toast.error(err?.response?.data?.detail || "Failed"); }
  };

  const submitNew = async (e) => {
    e.preventDefault();
    try {
      if (newKind === "trial_extension") {
        await api.post("/admin/approvals/trial-extension", {
          org_id: newForm.org_id, days: Number(newForm.days), reason: newForm.reason,
        });
      } else {
        await api.post("/admin/approvals/discount", {
          org_id: newForm.org_id, kind: newKind,
          discount_pct: newKind === "discount" ? Number(newForm.discount_pct) : null,
          valid_until: newForm.valid_until || null, reason: newForm.reason,
        });
      }
      toast.success("Approval request created");
      setOpenNew(false);
      setNewForm({ org_id: "", days: 7, discount_pct: 10, valid_until: "", reason: "" });
      load();
    } catch (err) { toast.error(err?.response?.data?.detail || "Failed"); }
  };

  return (
    <div className="p-6 space-y-6" data-testid="admin-approvals-page">
      <div className="flex items-end justify-between flex-wrap gap-4">
        <div>
          <div className="text-[10px] font-mono uppercase tracking-widest text-orange-600">// Approvals</div>
          <h1 className="text-3xl font-bold mt-1 text-foreground">Approvals queue</h1>
          <div className="text-muted-foreground text-sm mt-1">Trial extensions, discounts and complementary access — every request routed here.</div>
        </div>
        <div className="flex items-center gap-2">
          <Select value={status} onValueChange={setStatus}>
            <SelectTrigger className="w-40 h-9 rounded-sm bg-background text-foreground border-border" data-testid="approvals-status-filter">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="pending">Pending</SelectItem>
              <SelectItem value="approved">Approved</SelectItem>
              <SelectItem value="rejected">Rejected</SelectItem>
              <SelectItem value="all">All</SelectItem>
            </SelectContent>
          </Select>
          <Button onClick={() => setOpenBulk(true)} variant="outline"
                    className="rounded-sm border-border bg-card hover:bg-muted text-foreground gap-1.5"
                    data-testid="bulk-discount-btn">
            <Users className="w-4 h-4" /> Bulk discount
          </Button>
          <Button onClick={() => setOpenNew(true)} className="rounded-sm bg-gradient-to-r from-orange-600 to-red-600 hover:opacity-90 text-foreground gap-1.5" data-testid="new-approval-btn">
            <Plus className="w-4 h-4" /> New request
          </Button>
        </div>
      </div>

      <div className="grid grid-cols-1 gap-3">
        {loading && <div className="text-muted-foreground text-sm">Loading…</div>}
        {!loading && items.length === 0 && (
          <Card className="rounded-sm bg-card border-border text-foreground p-10 text-center">
            <CheckSquare className="w-10 h-10 mx-auto text-foreground/20" />
            <div className="mt-3 font-heading font-semibold text-lg">No {status} requests</div>
            <div className="text-muted-foreground mt-1 text-sm">Nothing waiting for review.</div>
          </Card>
        )}
        {items.map((a) => {
          const meta = KIND_META[a.kind] || KIND_META.discount;
          const Icon = meta.icon;
          return (
            <Card key={a.id} className={`rounded-sm border p-4 text-foreground ${meta.tint}`} data-testid={`approval-${a.id}`}>
              <div className="flex items-start gap-4">
                <div className="w-10 h-10 rounded-sm flex items-center justify-center bg-muted shrink-0">
                  <Icon className="w-5 h-5" />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <div className="text-[10px] font-mono uppercase tracking-widest">{meta.label}</div>
                    <span className={`inline-block px-2 py-0.5 rounded-sm text-[10px] font-mono uppercase tracking-widest ${STATUS_META[a.status]}`}>{a.status}</span>
                  </div>
                  <div className="font-heading font-semibold mt-1">{a.org_name}</div>
                  <div className="text-sm text-foreground/90 mt-1">
                    {a.kind === "trial_extension" && `Extend trial by ${a.days} days`}
                    {a.kind === "discount" && `${a.discount_pct}% discount${a.valid_until ? " until " + a.valid_until : ""}`}
                    {a.kind === "complementary" && `Complementary access${a.valid_until ? " until " + a.valid_until : " (perpetual)"}`}
                  </div>
                  {a.reason && <div className="text-xs text-muted-foreground mt-1.5 italic">"{a.reason}"</div>}
                  <div className="text-[10px] font-mono text-muted-foreground mt-2">
                    Requested by {a.requested_by} · {new Date(a.requested_at).toLocaleString()}
                    {a.decided_by && <> · Decided by {a.decided_by} · {new Date(a.decided_at).toLocaleString()}</>}
                  </div>
                  {a.decision_note && <div className="text-xs text-foreground/80 mt-1">Decision note: {a.decision_note}</div>}
                </div>
                {a.status === "pending" && (
                  <div className="flex flex-col gap-1.5">
                    <Button size="sm" onClick={() => decide(a, "approved")}
                            className="h-8 rounded-sm bg-emerald-600 hover:bg-emerald-500 text-foreground gap-1"
                            data-testid={`approve-${a.id}`}>
                      <Check className="w-3.5 h-3.5" /> Approve
                    </Button>
                    <Button size="sm" onClick={() => decide(a, "rejected")}
                            className="h-8 rounded-sm bg-red-600/70 hover:bg-red-500 text-foreground gap-1"
                            data-testid={`reject-${a.id}`}>
                      <X className="w-3.5 h-3.5" /> Reject
                    </Button>
                  </div>
                )}
              </div>
            </Card>
          );
        })}
      </div>

      <Dialog open={openNew} onOpenChange={setOpenNew}>
        <DialogContent className="rounded-sm sm:max-w-md">
          <DialogHeader><DialogTitle>Create approval request</DialogTitle></DialogHeader>
          <form onSubmit={submitNew} className="space-y-4 mt-2">
            <div>
              <Label className="text-xs">Type</Label>
              <Select value={newKind} onValueChange={setNewKind}>
                <SelectTrigger className="rounded-sm mt-1" data-testid="new-approval-kind"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="trial_extension">Trial extension</SelectItem>
                  <SelectItem value="discount">Discount (%)</SelectItem>
                  <SelectItem value="complementary">Complementary access</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-xs">Entity</Label>
              <Select value={newForm.org_id} onValueChange={(v) => setNewForm({ ...newForm, org_id: v })}>
                <SelectTrigger className="rounded-sm mt-1" data-testid="new-approval-org"><SelectValue placeholder="Pick an entity" /></SelectTrigger>
                <SelectContent className="max-h-80">
                  {entities.map((e) => (
                    <SelectItem key={e.id} value={e.id}>{e.name} · {e.plan_name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            {newKind === "trial_extension" && (
              <div>
                <Label className="text-xs">Days to extend</Label>
                <Input type="number" min={1} max={180} value={newForm.days}
                       onChange={(e) => setNewForm({ ...newForm, days: e.target.value })}
                       className="rounded-sm mt-1" data-testid="new-approval-days" />
              </div>
            )}
            {newKind === "discount" && (
              <div>
                <Label className="text-xs">Discount %</Label>
                <Input type="number" min={1} max={100} value={newForm.discount_pct}
                       onChange={(e) => setNewForm({ ...newForm, discount_pct: e.target.value })}
                       className="rounded-sm mt-1" data-testid="new-approval-pct" />
              </div>
            )}
            {(newKind === "discount" || newKind === "complementary") && (
              <div>
                <Label className="text-xs">Valid until (optional)</Label>
                <Input type="date" value={newForm.valid_until}
                       onChange={(e) => setNewForm({ ...newForm, valid_until: e.target.value })}
                       className="rounded-sm mt-1" data-testid="new-approval-until" />
              </div>
            )}
            <div>
              <Label className="text-xs">Reason (optional)</Label>
              <Textarea rows={3} value={newForm.reason} onChange={(e) => setNewForm({ ...newForm, reason: e.target.value })}
                        className="rounded-sm mt-1" data-testid="new-approval-reason"
                        placeholder="E.g., first-time customer, launch offer, non-profit…" />
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" className="rounded-sm" onClick={() => setOpenNew(false)}>Cancel</Button>
              <Button type="submit" className="rounded-sm bg-gradient-to-r from-orange-600 to-red-600 text-foreground" data-testid="new-approval-submit">
                Create request
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
      <Dialog open={openBulk} onOpenChange={(v) => { setOpenBulk(v); if (!v) setBulkPreview(null); }}>
        <DialogContent className="rounded-sm sm:max-w-2xl" data-testid="bulk-discount-dialog">
          <DialogHeader><DialogTitle>Bulk discount run</DialogTitle></DialogHeader>
          <div className="grid md:grid-cols-2 gap-4 mt-2">
            <div className="space-y-3">
              <div className="text-[10px] font-mono uppercase tracking-widest text-accent">// Discount</div>
              <div>
                <Label className="text-xs">Discount % *</Label>
                <Input type="number" min={1} max={100} value={bulkForm.discount_pct}
                       onChange={(e) => setBulkForm({ ...bulkForm, discount_pct: Number(e.target.value) })}
                       className="rounded-sm mt-1" data-testid="bulk-discount-pct" />
              </div>
              <div>
                <Label className="text-xs">Valid until (optional)</Label>
                <Input type="date" value={bulkForm.valid_until}
                       onChange={(e) => setBulkForm({ ...bulkForm, valid_until: e.target.value })}
                       className="rounded-sm mt-1" data-testid="bulk-discount-until" />
              </div>
              <div>
                <Label className="text-xs">Reason</Label>
                <Textarea rows={2} value={bulkForm.reason}
                        onChange={(e) => setBulkForm({ ...bulkForm, reason: e.target.value })}
                        placeholder="e.g. NGO discount, March cohort welcome offer…"
                        className="rounded-sm mt-1" data-testid="bulk-discount-reason" />
              </div>
            </div>
            <div className="space-y-3">
              <div className="text-[10px] font-mono uppercase tracking-widest text-accent">// Target filters (AND)</div>
              <div>
                <Label className="text-xs">Plans (comma-sep)</Label>
                <Input value={bulkForm.plan_ids.join(",")}
                       onChange={(e) => setBulkForm({ ...bulkForm, plan_ids: e.target.value.split(",").map((s) => s.trim()).filter(Boolean) })}
                       placeholder="solo, starter, growth" className="rounded-sm mt-1" data-testid="bulk-filter-plans" />
              </div>
              <div>
                <Label className="text-xs">Entity type</Label>
                <Select value={bulkForm.entity_type || "any"} onValueChange={(v) => setBulkForm({ ...bulkForm, entity_type: v === "any" ? "" : v })}>
                  <SelectTrigger className="rounded-sm mt-1" data-testid="bulk-filter-entity"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="any">Any</SelectItem>
                    <SelectItem value="proprietorship">Proprietorship</SelectItem>
                    <SelectItem value="partnership">Partnership</SelectItem>
                    <SelectItem value="company">Company</SelectItem>
                    <SelectItem value="llp">LLP</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-xs">Name contains</Label>
                <Input value={bulkForm.name_contains}
                       onChange={(e) => setBulkForm({ ...bulkForm, name_contains: e.target.value })}
                       placeholder="NGO, Foundation…" className="rounded-sm mt-1" data-testid="bulk-filter-name" />
              </div>
              <div className="grid grid-cols-2 gap-2">
                <div>
                  <Label className="text-xs">Created after</Label>
                  <Input type="date" value={bulkForm.created_after}
                         onChange={(e) => setBulkForm({ ...bulkForm, created_after: e.target.value })}
                         className="rounded-sm mt-1" data-testid="bulk-filter-after" />
                </div>
                <div>
                  <Label className="text-xs">Created before</Label>
                  <Input type="date" value={bulkForm.created_before}
                         onChange={(e) => setBulkForm({ ...bulkForm, created_before: e.target.value })}
                         className="rounded-sm mt-1" data-testid="bulk-filter-before" />
                </div>
              </div>
            </div>
          </div>

          {bulkPreview && (
            <div className="mt-3 p-3 rounded-sm border border-accent/25 bg-accent/5" data-testid="bulk-discount-preview">
              <div className="text-sm">
                <b>{bulkPreview.matched}</b> entities match. Sample:{" "}
                <span className="text-foreground/70">{bulkPreview.sample?.slice(0, 5).map((s) => s.name).join(", ")}{bulkPreview.sample?.length > 5 ? "…" : ""}</span>
              </div>
            </div>
          )}

          <DialogFooter className="mt-2">
            <Button type="button" variant="outline" className="rounded-sm" onClick={() => setOpenBulk(false)}>Cancel</Button>
            <Button type="button" variant="outline" className="rounded-sm" disabled={bulkBusy}
                    onClick={async () => {
                      setBulkBusy(true);
                      try { const r = await api.post("/admin/bulk-discount/preview", bulkForm); setBulkPreview(r.data); }
                      catch (err) { toast.error(err?.response?.data?.detail || "Preview failed"); }
                      finally { setBulkBusy(false); }
                    }} data-testid="bulk-discount-preview-btn">
              Preview matches
            </Button>
            <Button type="button" className="rounded-sm bg-gradient-to-r from-orange-600 to-red-600 text-foreground" disabled={bulkBusy || !bulkPreview || bulkPreview.matched === 0}
                    onClick={async () => {
                      if (!window.confirm(`Apply ${bulkForm.discount_pct}% discount to ${bulkPreview.matched} entities? This is not staged for approval — it applies immediately.`)) return;
                      setBulkBusy(true);
                      try {
                        const r = await api.post("/admin/bulk-discount/apply", bulkForm);
                        toast.success(`Applied to ${r.data.modified} entities`);
                        setOpenBulk(false); setBulkPreview(null); load();
                      } catch (err) { toast.error(err?.response?.data?.detail || "Apply failed"); }
                      finally { setBulkBusy(false); }
                    }} data-testid="bulk-discount-apply-btn">
              Apply to {bulkPreview?.matched || 0} entities
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
